import type { INetworkStatus } from '@erp/native/interfaces';

/**
 * Network status — strict state machine that refuses to flap.
 *
 * Going OFFLINE (any of these → instantly offline):
 *   - tracked fetch fails (network error from a real API call)
 *   - the OS fires `offline`
 *
 * Going BACK ONLINE (BOTH must happen):
 *   1. the OS fires `online` (gives us permission to attempt recovery)
 *   2. a tracked fetch then succeeds (confirms we really are back)
 *
 * Why both? `navigator.onLine` lies on Windows (stays true with dead
 * DNS) and the `online` event fires spuriously on WebView reconnects.
 * A SUCCESSFUL background fetch alone isn't proof either — the
 * Supabase keep-alive can succeed once between two real failures.
 * Combining the two signals is the only way to stop the
 * "banner appears and disappears every second" flap.
 *
 * "Tracked" means: not same-origin, not localhost, not Vite/HMR,
 * not data:/blob:.
 */

function shouldCountForOnlineDetection(url: string): boolean {
  try {
    if (!url) return false;
    if (/^(\/@vite|\/@react-refresh|\/@fs\/|\/node_modules\/\.vite\/)/.test(url)) {
      return false;
    }
    const u = new URL(url, typeof location !== 'undefined' ? location.href : 'http://localhost');
    if (u.protocol === 'data:' || u.protocol === 'blob:') return false;
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '::1') {
      return false;
    }
    if (typeof location !== 'undefined' && u.origin === location.origin) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function createWebNetwork(): INetworkStatus {
  const subs = new Set<(online: boolean) => void>();
  let online = typeof navigator !== 'undefined' ? navigator.onLine : true;
  // True when the OS has signalled `online` since the last `offline`,
  // but we have not yet seen a successful tracked fetch to confirm.
  // Only when both flags align (osArmedRecovery + tracked success) do
  // we flip our state back to online.
  let osArmedRecovery = false;

  const setState = (next: boolean) => {
    if (next === online) return;
    online = next;
    subs.forEach((cb) => {
      try {
        cb(online);
      } catch {
        /* subscriber crashed — ignore */
      }
    });
  };

  const goOffline = () => {
    osArmedRecovery = false;
    setState(false);
  };

  const onTrackedSuccess = () => {
    // We only listen to successes when offline AND the OS has armed
    // recovery. Any other success is just background noise.
    if (online) return;
    if (!osArmedRecovery) return;
    osArmedRecovery = false;
    setState(true);
  };

  if (typeof window !== 'undefined') {
    const origFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const url = String(typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url ?? '');
      const tracked = shouldCountForOnlineDetection(url);
      try {
        const res = await origFetch(...args);
        if (tracked) onTrackedSuccess();
        return res;
      } catch (err) {
        if (tracked) goOffline();
        throw err;
      }
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', () => {
      // Arm — don't flip yet. The next successful tracked fetch will.
      osArmedRecovery = true;
    });
  }

  return {
    isOnline: () => online,
    subscribe(cb) {
      subs.add(cb);
      try {
        cb(online);
      } catch {
        /* ignore */
      }
      return () => subs.delete(cb);
    },
    markRecovered() {
      // Caller has external evidence we're back. Skip the two-signal
      // gate and flip immediately.
      osArmedRecovery = false;
      setState(true);
    },
  };
}
